import { useFeatureFlagPayload } from '@posthog/react';
import {
  DEFAULT_LANDING_VARIANT,
  LANDING_VARIANTS,
  LandingVariant,
} from '../models/landing/variant';
import { AfterHours, isClosed } from '../utils/landing/afterHours';

export const LANDING_VARIANT_FLAG = 'beko-landing-variant';

/**
 * One arm of an A/B split: a design and its share of traffic. Weights are
 * relative (they need not sum to 100); an arm is picked in proportion to its
 * weight among the valid arms.
 */
export type SplitArm = { lp: LandingVariant; weight: number };

/**
 * The `beko-landing-variant` payload the business edits in PostHog: a map of
 * locale -> which landing experience that country should see. Editing it in the
 * PostHog UI changes the live page with no code deploy.
 *
 * A locale is either deterministic (`lp`) or A/B-tested (`split`):
 * - `lp` — every visitor in the locale sees that one design.
 * - `split` — each visitor is randomly assigned one arm, weighted. This is the
 *   per-country A/B test; `lp`-only locales are unaffected (backward compatible).
 *
 * Either way, an optional `afterHours` schedule (the contact centre's OPENING
 * hours in its own timezone) OVERRIDES the choice while the centre is closed —
 * i.e. the current time is outside every opening window — showing `afterHours.lp`
 * instead. So the A/B test is daytime-only; a closed call centre always wins.
 */
export type LandingVariantConfig = Record<
  string,
  { lp?: LandingVariant; split?: SplitArm[]; afterHours?: AfterHours } | undefined
>;

const isKnownVariant = (lp: unknown): lp is LandingVariant =>
  typeof lp === 'string' && LANDING_VARIANTS.includes(lp as LandingVariant);

/**
 * Weighted-random pick over a split's valid arms. Cookieless-safe: the
 * assignment is per page load (no device storage, so no consent implication),
 * which is acceptable for a short-dwell landing page — a reload may reassign the
 * arm. `rng` is injectable so tests are deterministic.
 *
 * Returns `undefined` when the split has no usable arm (unknown design, missing
 * or non-positive weight), so the caller falls back to the base `lp`/default.
 */
export const pickFromSplit = (
  split: SplitArm[] | undefined,
  rng: () => number = Math.random,
): LandingVariant | undefined => {
  const arms = (split ?? []).filter(
    (arm) => isKnownVariant(arm?.lp) && typeof arm.weight === 'number' && arm.weight > 0,
  );
  if (arms.length === 0) return undefined;

  const total = arms.reduce((sum, arm) => sum + arm.weight, 0);
  let threshold = rng() * total;
  for (const arm of arms) {
    threshold -= arm.weight;
    if (threshold < 0) return arm.lp;
  }
  // Floating-point guard: return the last arm if rounding overshoots.
  return arms[arms.length - 1].lp;
};

/**
 * Resolves the landing-page experience for the given locale from the
 * `beko-landing-variant` PostHog payload. Precedence:
 *
 * 1. **Base design** — if the locale has a `split`, the visitor is randomly
 *    assigned one arm (weighted); otherwise the deterministic `lp` is used.
 * 2. **After-hours override** — if the locale has an `afterHours` schedule and
 *    the centre is closed now, `afterHours.lp` replaces the base (so the A/B
 *    test is daytime-only; a closed call centre always wins).
 * 3. **Fallback** — an unknown/absent result falls back to the default
 *    (`phone-and-link`), so the page always has a working CTA (also covers the
 *    flag being unavailable: PostHog down, flag off).
 *
 * The split arm is assigned ONCE PER PAGE LOAD and shared by every hook
 * instance (see `assignFromSplit`); `initPostHog()` blocks render until the
 * flags response arrives, so the payload is present on first render.
 */
/**
 * One split assignment per page load, shared by EVERY consumer of the hook.
 *
 * Layout, LandingPage and PostHogPageView each resolve the variant. With a
 * per-instance pick (the previous `useMemo`) each rolled its own dice, so the
 * page could render one arm while analytics registered another — the first
 * simulated A/B run (2026-08-28) showed ~50% of `cta_click`s disagreeing with
 * `configured_lp`, plus duplicate `$pageview`s whenever the flag payload object
 * was re-created and the memo re-rolled. Keying the assignment on the locale
 * and the split's CONTENT (not object identity) makes it stable for the life of
 * the page load, cookieless-safe (nothing stored on the device), and identical
 * across instances. A locale switch (new key) or a changed split gets a fresh
 * draw, as before.
 */
const splitAssignments = new Map<string, LandingVariant>();

/** Test hook: forget page-load assignments between specs. */
export const resetSplitAssignments = (): void => splitAssignments.clear();

const assignFromSplit = (locale: string, split: SplitArm[]): LandingVariant | undefined => {
  const key = `${locale}|${JSON.stringify(split)}`;
  const existing = splitAssignments.get(key);
  if (existing) return existing;
  const picked = pickFromSplit(split);
  if (picked) splitAssignments.set(key, picked);
  return picked;
};

export const useLandingVariant = (locale: string): LandingVariant => {
  const config = useFeatureFlagPayload(LANDING_VARIANT_FLAG) as LandingVariantConfig | undefined;
  const entry = config?.[locale];

  // A split locale gets the page-load-wide assignment; a locale without a
  // split keeps its deterministic `lp`.
  const base = entry?.split?.length ? assignFromSplit(locale, entry.split) : entry?.lp;

  const lp =
    entry?.afterHours && isClosed(entry.afterHours, new Date()) ? entry.afterHours.lp : base;

  return isKnownVariant(lp) ? lp : DEFAULT_LANDING_VARIANT;
};
